import "./datatable.scss";
import { DataGrid } from "@mui/x-data-grid";
import { Button } from "@mui/material";
import { useEffect, useState } from "react";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const Datatable = ({
  columnsData,
  rowData,
  pageTitle,
  actionColumn,
  addBtnTxt,
  onAddBtn,
  rowsPerPage = 12,
  showPagination = true,
  hideTitleBar = false,
  autoHeight = false,
  scrollable = false,
  /** @type {{ field: string, sort: 'asc' | 'desc' }[]} */
  defaultSortModel = [],
}) => {
  const safeRows = Array.isArray(rowData) ? rowData : [];
  const [page, setPage] = useState(0);

  const totalPages = Math.max(1, Math.ceil(safeRows.length / rowsPerPage));
  const safePage = clamp(page, 0, totalPages - 1);

  useEffect(() => {
    setPage(0);
  }, [safeRows.length, rowsPerPage]);

  useEffect(() => {
    if (page !== safePage) {
      setPage(safePage);
    }
  }, [safePage, page]);

  const startIndex = safePage * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedRows = showPagination
    ? safeRows.slice(startIndex, endIndex)
    : safeRows;
  const displayRows = paginatedRows.map((row, idx) => ({
    ...row,
    sno: startIndex + idx + 1,
  }));
  const startLabel = safeRows.length > 0 ? startIndex + 1 : 0;
  const endLabel = Math.min(endIndex, safeRows.length);

  return (
    <div className="datatable">
      {!hideTitleBar && (
        <div className="datatableTitle">
          {pageTitle}
          {addBtnTxt && (
            <Button onClick={onAddBtn} size="small" variant="outlined">
              {addBtnTxt}
            </Button>
          )}
        </div>
      )}

      <div
        className={`datagridWrap${autoHeight ? " datagridWrap--autoHeight" : ""}${scrollable ? " datagridWrap--scrollable" : ""}`}
      >
        <DataGrid
          className="datagrid"
          rows={displayRows}
          columns={columnsData.concat(actionColumn)}
          getRowId={(row) => row.id ?? row.transaction_id}
          checkboxSelection={false}
          disableColumnSelector
          disableRowSelectionOnClick
          hideFooter
          pageSize={rowsPerPage}
          rowHeight={52}
          density="comfortable"
          autoHeight={autoHeight}
          initialState={{
            sorting: {
              sortModel: defaultSortModel,
            },
          }}
        />
      </div>

      {showPagination && (
        <div className="datatablePagination">
          <button
            type="button"
            className="paginationBtn"
            onClick={() =>
              setPage((current) => clamp(current - 1, 0, totalPages - 1))
            }
            disabled={safePage === 0}
          >
            Previous
          </button>
          <span>
            Showing {startLabel}-{endLabel} of {safeRows.length}
          </span>
          <button
            type="button"
            className="paginationBtn"
            onClick={() =>
              setPage((current) => clamp(current + 1, 0, totalPages - 1))
            }
            disabled={safePage === totalPages - 1}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Datatable;
